/**
 * Phase 1.5 — returning-user home modules (logged-in only).
 * Composes EXISTING data: alert-center (since last visit), saved
 * searches (continue searching), favorites, localStorage recents.
 * No new backend state — reuse, don't duplicate.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, Bell, Heart, History, List as ListIcon, Flame, TrendingDown } from "lucide-react";
import { fetchAlertCenter } from "../../api/alertCenterApi";
import { listSavedSearches } from "../../api/savedSearchesApi";
import { listFavorites } from "../../api/dealflowApi";
import { criteriaChips } from "../alerts/SaveAlertDialog";

function Strip({ title, to, toLabel, children, testid }) {
  return (
    <section data-testid={testid} className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-zinc-200 md:text-lg">{title}</h2>
        {to && (
          <Link to={to} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
            {toLabel} <ArrowRight size={12} aria-hidden />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function SinceLastVisit({ ac, t }) {
  if (!ac) return null;
  const newMatches = (ac.new_matches || []).reduce(
    (s, g) => s + (g.new_count || g.count || (g.items || []).length || 0), 0,
  );
  const changes = ac.price_drops || [];
  const drops = changes.filter((d) => d.direction !== "up").length;
  const favChanged = changes.filter((d) => d.matched_via === "favorite").length;
  const chips = [
    newMatches > 0 && { icon: Bell, label: t("personal.newMatches", { count: newMatches }), to: "/alerts" },
    drops > 0 && { icon: TrendingDown, label: t("personal.priceDrops", { count: drops }), to: "/favorites" },
    favChanged > 0 && { icon: Heart, label: t("personal.favChanged", { count: favChanged }), to: "/favorites" },
  ].filter(Boolean);
  if (!chips.length) return null;
  return (
    <section data-testid="home-since-visit" className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-400">
        {t("personal.sinceVisit")}
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((c, i) => (
          <Link
            key={i}
            to={c.to}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-zinc-900/70 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-emerald-400/60"
          >
            <c.icon size={12} className="text-emerald-400" aria-hidden />
            {c.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function searchChipLabel(s, t) {
  // Befund 12.06.2026: der Name eines Suchauftrags ist freier Text
  // („suv elektro") und beweist kein Kriterium — deshalb immer die
  // GESPEICHERTEN Kriterien danebenstellen.
  const crit = criteriaChips(s.vertical === "auto" ? "auto" : "immo", s.filters, t)
    .map((c) => c.label)
    .join(" · ");
  return [s.name, crit || t("personal.noCriteria", { defaultValue: "—" })]
    .filter(Boolean)
    .join(" · ");
}

export default function HomePersonal({ onRunRecent, onExplore }) {
  const { t } = useTranslation(["discover", "heatmap", "common"]);
  const navigate = useNavigate();
  const acQ = useQuery({
    queryKey: ["alert-center-home"],
    queryFn: fetchAlertCenter,
    staleTime: 60_000,
    retry: false,
  });
  const ssQ = useQuery({
    queryKey: ["saved-searches-home"],
    queryFn: listSavedSearches,
    staleTime: 60_000,
    retry: false,
  });
  const favQ = useQuery({
    queryKey: ["favorites-home"],
    queryFn: () => listFavorites({ limit: 6 }),
    staleTime: 60_000,
    retry: false,
  });

  const recents = [];
  const saved = (ssQ.data?.items || []).slice(0, 4);
  const acGroups = acQ.data?.new_matches || [];
  const newBySearch = {};
  acGroups.forEach((g) => {
    // API-Vertrag: `alert_ulid` + `count` (alert_center.py). Die alten
    // Schlüssel `saved_search_ulid`/`new_count` existieren nicht — das
    // „neu"-Abzeichen blieb deshalb immer leer.
    if (g.alert_ulid) {
      newBySearch[g.alert_ulid] = g.count || (g.items || []).length || 0;
    }
  });
  const favorites = (favQ.data?.items || []).slice(0, 6);
  const hasContinue = saved.length > 0 || recents.length > 0;
  const newForSearches = acGroups
    .flatMap((g) => {
      const crit = criteriaChips(g.vertical === "auto" ? "auto" : "immo", g.filters, t)
        .map((c) => c.label)
        .join(" · ");
      return (g.items || []).map((it) => ({
        ...it, searchName: g.name, searchCriteria: crit,
      }));
    })
    .filter((it) => it.ulid)
    .slice(0, 6);

  return (
    <div className="space-y-5" data-testid="home-personal">
      <SinceLastVisit ac={acQ.data} t={t} />

      {hasContinue && (
        <Strip title={t("personal.continue")} to="/alerts" toLabel={t("personal.manage")} testid="home-continue">
          <div className="flex flex-wrap gap-2">
            {saved.map((s) => (
              <button
                key={s.ulid}
                type="button"
                data-testid={`continue-saved-${s.ulid}`}
                onClick={() => navigate("/alerts")}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900/70 px-3 py-2 text-xs text-zinc-200 hover:border-zinc-500"
              >
                <Bell size={12} className="text-emerald-400" aria-hidden />
                {searchChipLabel(s, t)}
                {newBySearch[s.ulid] > 0 && (
                  <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">
                    {t("personal.newBadge", { count: newBySearch[s.ulid] })}
                  </span>
                )}
              </button>
            ))}
            {recents.map((r, i) => (
              <button
                key={`r-${i}`}
                type="button"
                data-testid={`continue-recent-${i}`}
                onClick={() => onRunRecent?.(r)}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
              >
                <History size={12} aria-hidden />
                {r.label}
              </button>
            ))}
          </div>
        </Strip>
      )}

      {newForSearches.length > 0 && (
        <Strip
          title={t("personal.newForSearches")}
          to="/alerts"
          toLabel={t("personal.all")}
          testid="home-new-for-searches"
        >
          <div className="flex gap-3 overflow-x-auto pb-1">
            {newForSearches.map((it) => (
              <Link
                key={it.ulid}
                to={`/listing/${it.ulid}`}
                data-testid={`new-match-card-${it.ulid}`}
                className="w-44 shrink-0 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/60 hover:border-zinc-600"
              >
                <div className="aspect-[4/3] w-full bg-zinc-800">
                  {it.primary_image_url && (
                    <img src={it.primary_image_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-zinc-100">
                      {typeof it.price === "number" ? `${Math.round(it.price).toLocaleString("de-AT")} €` : "—"}
                    </span>
                    {typeof it.price_vs_market_pct === "number" && it.price_vs_market_pct < 0 && (
                      <span className="rounded bg-emerald-500/15 px-1 py-0.5 text-[10px] font-bold text-emerald-300">
                        {it.price_vs_market_pct.toFixed(1).replace(".", ",")} %
                      </span>
                    )}
                  </div>
                  <div className="line-clamp-1 text-[11px] text-zinc-500">{it.title}</div>
                  {it.searchName && (
                    <div className="mt-0.5 line-clamp-1 text-[10px] text-emerald-500/80">{it.searchName}</div>
                  )}
                  {it.searchCriteria && (
                    <div
                      data-testid={`new-match-criteria-${it.ulid}`}
                      className="line-clamp-1 text-[10px] text-zinc-500"
                    >
                      {it.searchCriteria}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </Strip>
      )}

      {favorites.length > 0 && (
        <Strip title={t("personal.favorites")} to="/favorites" toLabel={t("personal.all")} testid="home-favorites">
          <div className="flex gap-3 overflow-x-auto pb-1">
            {favorites.map((c) => (
              <Link
                key={c.id || c.listing_ulid}
                to={`/listing/${c.id || c.listing_ulid}`}
                className="w-44 shrink-0 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/60 hover:border-zinc-600"
              >
                <div className="aspect-[4/3] w-full bg-zinc-800">
                  {(c.primary_image_url || c.image_url) && (
                    <img
                      src={c.primary_image_url || c.image_url}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="p-2">
                  <div className="text-sm font-bold text-zinc-100">
                    {c.price_display || (typeof c.price === "number" ? `${Math.round(c.price).toLocaleString("de-AT")} €` : "—")}
                  </div>
                  <div className="line-clamp-1 text-[11px] text-zinc-500">{c.title}</div>
                </div>
              </Link>
            ))}
            <Link
              to="/favorites"
              className="flex w-24 shrink-0 items-center justify-center rounded-xl border border-dashed border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
            >
              <Heart size={16} aria-hidden />
            </Link>
          </div>
        </Strip>
      )}

      {/* Markt entdecken — Liste / Heatmap (der alte „Karte"-Button ist
          entfernt: die Karten-Ansicht wurde in den Heatmap-Explorer
          konsolidiert, ein funktionsloser Toggle bleibt nicht stehen). */}
      <section data-testid="home-explore" className="space-y-2">
        <h2 className="text-base font-semibold text-zinc-200 md:text-lg">{t("personal.explore")}</h2>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="explore-list"
            onClick={() => onExplore?.("list")}
            className="flex items-center justify-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-3 text-xs font-medium text-zinc-300 hover:border-zinc-600"
          >
            <ListIcon size={14} className="text-emerald-400" aria-hidden /> {t("personal.exploreList")}
          </button>
          <Link
            to="/heatmap"
            data-testid="explore-heatmap"
            className="flex items-center justify-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-3 text-xs font-medium text-zinc-300 hover:border-zinc-600"
          >
            <Flame size={14} className="text-amber-400" aria-hidden /> {t("personal.exploreHeatmap")}
          </Link>
        </div>
      </section>
    </div>
  );
}
